import React, { createContext, useContext, useState, useCallback } from 'react';
import MinimizedModalsWidget from '../components/UI/MinimizedModalsWidget';

const ModalMinimizerContext = createContext(null);

export const ModalMinimizerProvider = ({ children }) => {
    const [minimizedModals, setMinimizedModals] = useState([]);
    const [pendingRestore, setPendingRestore] = useState(null);

    const minimizeModal = useCallback((item) => {
        // item: { id, title, subtitle, icon, route, formData, onExpand, onClose }
        const currentRoute = item.route || window.location.pathname;
        const newItem = { ...item, route: currentRoute };

        setMinimizedModals((prev) => {
            const exists = prev.some((m) => m.id === item.id);
            if (exists) {
                return prev.map((m) => (m.id === item.id ? { ...m, ...newItem } : m));
            }
            return [...prev, newItem];
        });
    }, []);

    const restoreModal = useCallback((id) => {
        let targetItem = null;
        setMinimizedModals((prev) => {
            const target = prev.find((m) => m.id === id);
            if (target) {
                targetItem = target;
            }
            return prev.filter((m) => m.id !== id);
        });

        if (!targetItem) return;

        const currentPath = window.location.pathname;
        const targetRoute = targetItem.route || currentPath;
        const isSameRoute = currentPath === targetRoute || currentPath.startsWith(targetRoute);

        if (isSameRoute && targetItem.onExpand) {
            try {
                targetItem.onExpand();
                return;
            } catch (e) {
                // If onExpand fails (e.g. unmounted component), fallback to pending restore
            }
        }

        // Set pending restore with stored formData and navigate to original route
        setPendingRestore({ id: targetItem.id, formData: targetItem.formData, route: targetRoute });
        if (!isSameRoute) {
            if (window.history && window.history.pushState) {
                window.history.pushState({}, '', targetRoute);
                window.dispatchEvent(new Event('popstate'));
            } else {
                window.location.href = targetRoute;
            }
        }
    }, []);

    const removeMinimizedModal = useCallback((id) => {
        setMinimizedModals((prev) => {
            const target = prev.find((m) => m.id === id);
            if (target && target.onClose) {
                try { target.onClose(); } catch (e) {}
            }
            return prev.filter((m) => m.id !== id);
        });
        setPendingRestore((prev) => (prev && prev.id === id ? null : prev));
    }, []);

    const consumePendingRestore = useCallback((id) => {
        if (pendingRestore && pendingRestore.id === id) {
            const data = pendingRestore.formData;
            setPendingRestore(null);
            return data || {};
        }
        return null;
    }, [pendingRestore]);

    return (
        <ModalMinimizerContext.Provider
            value={{
                minimizedModals,
                minimizeModal,
                restoreModal,
                removeMinimizedModal,
                pendingRestore,
                consumePendingRestore,
            }}
        >
            {children}
            <MinimizedModalsWidget />
        </ModalMinimizerContext.Provider>
    );
};

export const useModalMinimizer = () => {
    const ctx = useContext(ModalMinimizerContext);
    if (!ctx) {
        return {
            minimizedModals: [],
            minimizeModal: () => {},
            restoreModal: () => {},
            removeMinimizedModal: () => {},
            pendingRestore: null,
            consumePendingRestore: () => null,
        };
    }
    return ctx;
};
